import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Calendar } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import DocumentScannerBox from "../../components/DocumentScannerBox";

const CATEGORIES = ["carburante", "buono_pasto", "stipendio", "manutenzione", "utenze", "tasse", "altro"];

const CAT_COLORS = {
  carburante: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  buono_pasto: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  stipendio: "bg-[#ff0033]/15 text-[#ff5577] border-[#ff0033]/40",
  manutenzione: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  utenze: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  tasse: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  altro: "bg-white/10 text-white/60 border-white/20",
};

export default function Expenses() {
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ data: new Date().toISOString().slice(0, 10), categoria: "carburante", descrizione: "", importo: 0, fornitore: "", metodo_pagamento: "contanti" });

  const load = () => api.get("/expenses").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, importo: +form.importo });
      toast.success("Spesa registrata");
      setShow(false);
      setForm({ data: new Date().toISOString().slice(0, 10), categoria: "carburante", descrizione: "", importo: 0, fornitore: "", metodo_pagamento: "contanti" });
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la spesa?")) return;
    await api.delete(`/expenses/${id}`); load();
  };

  // Weekly grouping
  const today = new Date();
  const weekStart = new Date(today); weekStart.setDate(today.getDate() - (today.getDay() === 0 ? 6 : today.getDay() - 1));
  const labels = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  const weekDays = labels.map((l, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const dayExpenses = list.filter((e) => e.data === iso);
    return { label: l, date: iso, expenses: dayExpenses, total: dayExpenses.reduce((s, e) => s + e.importo, 0) };
  });
  const weekTotal = weekDays.reduce((s, d) => s + d.total, 0);
  const monthTotal = list.filter((e) => e.data?.startsWith(today.toISOString().slice(0, 7))).reduce((s, e) => s + e.importo, 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Spese</h1>
          <p className="text-sm text-white/45 mt-1">Carburante · Buoni pasto · Stipendi · Manutenzioni · Utenze</p>
        </div>
        <button data-testid="exp-add" onClick={() => setShow(true)} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
          <Plus size={14} /> Nuova Spesa
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Spese Settimana</div>
          <div className="font-display text-3xl font-extrabold neon-text mt-2">€{weekTotal.toLocaleString("it-IT")}</div>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Spese Mese</div>
          <div className="font-display text-3xl font-extrabold mt-2">€{monthTotal.toLocaleString("it-IT")}</div>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Totale Voci</div>
          <div className="font-display text-3xl font-extrabold mt-2">{list.length}</div>
        </div>
      </div>

      {/* Weekly grid - Excel style */}
      <div className="glass rounded-2xl p-5 overflow-x-auto">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Settimana Corrente · Lun → Dom</div>
        <div className="grid grid-cols-7 gap-2 min-w-[900px]">
          {weekDays.map((d) => (
            <div key={d.date} className="rounded-xl bg-white/[0.02] border border-white/5 p-3 min-h-[160px]">
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-display font-bold text-sm">{d.label}</div>
                <div className="text-[10px] text-white/40 font-mono">{d.date.slice(5)}</div>
              </div>
              <div className="space-y-1.5">
                {d.expenses.map((e) => (
                  <div key={e.id} className="text-[10px] flex items-start justify-between gap-1">
                    <div className="min-w-0">
                      <div className="truncate">{e.descrizione}</div>
                      <div className="text-white/40 truncate">{e.categoria.replace('_', ' ')}</div>
                    </div>
                    <div className="font-mono font-bold text-[#ff0033]">€{e.importo}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between">
                <span className="text-[9px] uppercase tracking-widest text-white/40">Tot</span>
                <span className="font-display font-bold text-sm">€{d.total}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">Cumulo Settimana</span>
          <span className="font-display text-2xl font-extrabold neon-text">€{weekTotal.toLocaleString("it-IT")}</span>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">Data</th>
              <th className="text-left p-4">Categoria</th>
              <th className="text-left p-4">Descrizione</th>
              <th className="text-left p-4">Fornitore</th>
              <th className="p-4">Metodo</th>
              <th className="p-4 text-right">Importo</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.map((e) => (
              <tr key={e.id} className="hover:bg-white/[0.02]" data-testid={`exp-${e.id}`}>
                <td className="p-4 font-mono text-xs">{e.data}</td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${CAT_COLORS[e.categoria]}`}>{e.categoria.replace('_', ' ')}</span>
                </td>
                <td className="p-4">{e.descrizione}</td>
                <td className="p-4 text-white/60">{e.fornitore || '—'}</td>
                <td className="p-4 text-center text-xs uppercase tracking-wider">{e.metodo_pagamento}</td>
                <td className="p-4 text-right font-display font-bold">€{e.importo}</td>
                <td className="p-4 text-right">
                  <button onClick={() => remove(e.id)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-white/40">Nessuna spesa registrata.</td></tr>}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="exp-form">
            <div className="font-display text-2xl font-bold mb-5">Nuova Spesa</div>

            {/* AI Scanner Scontrino/Fattura */}
            <div className="mb-4">
              <DocumentScannerBox
                testIdPrefix="exp-scanner"
                context="damages"
                variant="full"
                title="Scansione AI · Scontrino / Ricevuta / Fattura"
                description="Carica foto scontrino o PDF fattura: l'AI estrae data, fornitore, descrizione, importo."
                onResult={(d) => {
                  const persona = d?.persona || {};
                  const noleggio = d?.noleggio || {};
                  const note = d?.note || d?.documenti_estratti?.[0]?.note || "";
                  setForm((f) => ({
                    ...f,
                    data: noleggio.data_inizio || f.data,
                    descrizione: note || f.descrizione,
                    fornitore: persona.ragione_sociale || persona.nome || f.fornitore,
                    importo: parseFloat(noleggio.totale_eur || 0) || f.importo,
                  }));
                }}
              />
            </div>

            <div className="space-y-3">
              <input required type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
              </select>
              <input required placeholder="Descrizione (es. Pieno Tesla Model S)" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <input placeholder="Fornitore (opzionale)" value={form.fornitore} onChange={(e) => setForm({ ...form, fornitore: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <input required type="number" step="0.01" placeholder="Importo €" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })}
                  className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                <select value={form.metodo_pagamento} onChange={(e) => setForm({ ...form, metodo_pagamento: e.target.value })}
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {["contanti", "pos", "bonifico", "carta"].map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="exp-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">Registra</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
